import { useMemo } from 'react';
import { Button, Group, Modal, Stack, Text } from '@cowbell/ui';
import { useActor } from '../../../hooks/useActor';
import { useIntegrationCatalog } from '../../hooks/useIntegrations';
import { ProviderIcon } from '../ProviderIcon';
import { describeScopes } from './connect-integration.copy';
import styles from './ConnectIntegrationModal.module.css';

/** The auth method carrying the per-user OAuth scopes on a catalog entry. */
const USER_OAUTH_METHOD = 'oauth2_user';

export interface ConnectIntegrationModalProps {
  readonly opened: boolean;
  readonly provider: string;
  readonly displayName: string;
  readonly isConnecting: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

export function ConnectIntegrationModal({
  opened,
  provider,
  displayName,
  isConnecting,
  onCancel,
  onConfirm,
}: ConnectIntegrationModalProps) {
  const { data: catalog } = useIntegrationCatalog();
  const { email } = useActor();

  const scopes = useMemo(() => {
    const entry = catalog?.find((e) => e.provider === provider);
    const method = entry?.auth_methods?.find(
      (m) => m.method === USER_OAUTH_METHOD
    );
    return describeScopes(method?.scopes ?? []);
  }, [catalog, provider]);

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={null}
      centered
      // The AskMoo widget sits at z-index 1100, so a default-z modal renders
      // behind it.
      zIndex={1400}
      // A global app style resolves the Mantine modal inner's
      // inset-inline-start to 100%, putting the dialog a full viewport-width
      // to the right so only the overlay shows.
      styles={{ inner: { left: 0, right: 0 } }}
    >
      <Stack gap="md" className={styles.body}>
        <Group justify="center">
          <ProviderIcon
            provider={provider}
            displayName={displayName}
            size={48}
          />
        </Group>

        <Text fw={700} size="lg" ta="center">
          Connect {displayName}?
        </Text>

        {email && (
          <Group justify="center">
            <div className={styles.accountChip}>
              <Text size="sm">{email}</Text>
            </div>
          </Group>
        )}

        {/* Guarded on the total, not on how many we could describe: when NO
            scope has vetted copy the user must still be told access is being
            requested. Hiding the whole block there would be silent in exactly
            the case they most need to hear about. */}
        {scopes.described.length + scopes.undescribedCount > 0 && (
          <Stack gap={4}>
            <Text fw={600} size="sm">
              What this lets Moo do:
            </Text>
            {scopes.described.length > 0 && (
              <ul className={styles.scopeList}>
                {scopes.described.map((line) => (
                  <li key={line}>
                    <Text size="sm" c="dimmed">
                      {line}
                    </Text>
                  </li>
                ))}
              </ul>
            )}
            {scopes.undescribedCount > 0 && (
              <Text size="xs" c="dimmed">
                {/* "additional" only makes sense relative to a list shown
                    above; with nothing described these are simply the
                    permissions being requested. */}
                {scopes.described.length > 0
                  ? `${displayName} also requests ${scopes.undescribedCount} additional `
                  : `${displayName} requests ${scopes.undescribedCount} `}
                {scopes.undescribedCount === 1 ? 'permission' : 'permissions'}.
                You&apos;ll see the full list on {displayName}&apos;s own
                consent screen.
              </Text>
            )}
          </Stack>
        )}

        <Text size="xs" c="dimmed">
          You&apos;ll finish signing in on {displayName}&apos;s site. This
          connection is yours alone — you can disconnect it any time from
          Connected Apps.
        </Text>

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={isConnecting}>
            Connect
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
